import React from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {defineMessages} from 'react-intl';
import type {EditMode} from '@igp/shared/components/button';
import {EditableSelectInternal, EditableTextInternal} from '@igp/shared/components/editable';
import {LocalizedText} from '@igp/shared/components/i18n';
import {FormError, RuleType, useValidationFormatter} from '@igp/shared/components/input';
import type {SelectOption} from '@igp/shared/components/select';
import {HeaderCell} from '@igp/shared/components/table';
import type {
    AgentRevenueShareViewModel,
    CasinoSubTypesViewModel,
    GlobalSpinsSubTypesViewModel,
    PokerCashPloSubTypesViewModel,
    PokerCashSubTypesViewModel,
    PokerMttSubTypesViewModel,
    SportsSubTypesViewModel,
} from '@igp/shared/features/user-profile';
import type {CommissionAllSubTypesViewModel} from '@igp/shared/features/user-profile';
import {casinoSubTypesValues, pokerCashSubTypesValues, pokerMttSubTypesValues} from '@igp/shared/features/user-profile';
import {globalSpinsSubTypesValues, sportsSubTypesValues} from '@igp/shared/features/user-profile';
import {InputAdornment, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material';
import {makeStyles} from 'tss-react/mui';

const localized = defineMessages({
    pokerCash: {
        id: 'RevenueShareManagementTable_pokerCash',
        defaultMessage: 'Poker Cash',
    },
    pokerMtt: {
        id: 'RevenueShareManagementTable_pokerMtt',
        defaultMessage: 'Poker MTT',
    },
    plo: {
        id: 'RevenueShareManagementTable_plo',
        defaultMessage: 'PLO',
    },
    casino: {
        id: 'RevenueShareManagementTable_casino',
        defaultMessage: 'Casino',
    },
    globalSpins: {
        id: 'RevenueShareManagementTable_globalSpins',
        defaultMessage: 'Global Spins',
    },
    sports: {
        id: 'RevenueShareManagementTable_sports',
        defaultMessage: 'Sports',
    },
    ratio: {
        id: 'RevenueShareManagementTable_ration',
        defaultMessage: 'Ratio %',
    },
    commissionType: {
        id: 'RevenueShareManagementTable_commissionType',
        defaultMessage: 'Commission Type',
    },
    none: {
        id: 'RevenueShareManagementTable_none',
        defaultMessage: 'None',
    },
});

const subTypesLocalization = defineMessages<CommissionAllSubTypesViewModel>({
    PokerNgr: {
        id: 'RevenueShareManagementTable_PokerNgr',
        defaultMessage: 'NGR',
    },
    PokerGgr: {
        id: 'RevenueShareManagementTable_PokerGgr',
        defaultMessage: 'GGR',
    },
    PokerGgrNoInsurance: {
        id: 'RevenueShareManagementTable_PokerGgrNoInsurance',
        defaultMessage: 'GGR (No Insurance)',
    },
    MttGgr: {
        id: 'RevenueShareManagementTable_MttGgr',
        defaultMessage: 'GGR',
    },
    PloGgr: {
        id: 'RevenueShareManagementTable_PloGgr',
        defaultMessage: 'GGR',
    },
    CasinoGgrAfterExternalCosts: {
        id: 'RevenueShareManagementTable_CasinoGgrAgterExternalCosts',
        defaultMessage: 'GGR (After External Costs)',
    },
    CasinoGgr: {
        id: 'RevenueShareManagementTable_CasinoGgr',
        defaultMessage: 'GGR',
    },
    CasinoWager: {
        id: 'RevenueShareManagementTable_CasinoWager',
        defaultMessage: 'Wager',
    },
    SportsWager: {
        id: 'RevenueShareManagementTable_SportsWager',
        defaultMessage: 'Wager',
    },
    SportsGgr: {
        id: 'RevenueShareManagementTable_SportsGgr',
        defaultMessage: 'GGR',
    },
    GlobalSpinsGgr: {
        id: 'RevenueShareManagementTable_GlobalSpinsGgr',
        defaultMessage: 'GGR',
    },
});

const useStyles = makeStyles()(() => ({
    select: {
        '& > div': {
            flexGrow: 1,
        },
    },
    tableRow: {
        '& > .MuiTableCell-root': {
            minWidth: '120px',
        },
    },
}));

type RevenueShareManagementTableProps = {
    mode: EditMode;
    isMasterAgent: boolean;
};

export function RevenueShareManagementTable({mode = 'Read', isMasterAgent = false}: RevenueShareManagementTableProps) {
    const {control, setValue, watch, trigger} = useFormContext<AgentRevenueShareViewModel>();
    const subtypesMode: EditMode = isMasterAgent && mode ? mode : 'Read';
    const validationFormatter = useValidationFormatter();
    const {classes} = useStyles();
    const maxLimits: Record<CommissionAllSubTypesViewModel, number> = {
        PokerNgr: 100,
        PokerGgr: 100,
        PokerGgrNoInsurance: 100,
        MttGgr: 100,
        PloGgr: 100,
        CasinoGgrAfterExternalCosts: 100,
        CasinoGgr: 100,
        CasinoWager: 1.5,
        SportsWager: 1.5,
        SportsGgr: 100,
        GlobalSpinsGgr: 100,
    };
    const pokerCashToPloMapping: Record<PokerCashSubTypesViewModel, PokerCashPloSubTypesViewModel> = {
        PokerGgr: null,
        PokerGgrNoInsurance: null,
        PokerNgr: 'PloGgr',
    };
    const pokerPloCashSubtype = watch('commission_subtypes.poker_cash_plo');
    return (
        <Table>
            <TableHead>
                <TableRow className={classes.tableRow}>
                    <HeaderCell></HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.pokerCash} />
                    </HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.pokerMtt} />
                    </HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.plo} />
                    </HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.casino} />
                    </HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.globalSpins} />
                    </HeaderCell>
                    <HeaderCell>
                        <LocalizedText label={localized.sports} />
                    </HeaderCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow className={classes.tableRow}>
                    <HeaderCell>
                        <LocalizedText label={localized.ratio} />
                    </HeaderCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="poker_cash.nlh_sd"
                            rules={{
                                required: validationFormatter(RuleType.Required, localized.pokerCash),
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.pokerCash, 0)},
                                validate: (value, values) => {
                                    const maxValue = maxLimits[values?.commission_subtypes?.poker] ?? 100;
                                    return value > maxValue ? validationFormatter(RuleType.Max, localized.pokerCash, maxValue) : true;
                                },
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="poker_mtt"
                            rules={{
                                required: validationFormatter(RuleType.Required, localized.pokerMtt),
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.pokerMtt, 0)},
                                validate: (value, values) => {
                                    const maxValue = maxLimits[values?.commission_subtypes?.poker_mtt] ?? 100;
                                    return value > maxValue ? validationFormatter(RuleType.Max, localized.pokerMtt, maxValue) : true;
                                },
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                        disabled={pokerPloCashSubtype !== 'PloGgr'}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="poker_cash.plo"
                            rules={{
                                required: !pokerPloCashSubtype ? false : validationFormatter(RuleType.Required, localized.plo),
                                validate: {
                                    max: (value, values) => {
                                        const maxValue = maxLimits[values?.commission_subtypes?.poker_cash_plo] ?? 100;
                                        return value > maxValue ? validationFormatter(RuleType.Max, localized.plo, maxValue) : true;
                                    },
                                },
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.plo, 0)},
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="casino_share"
                            rules={{
                                required: validationFormatter(RuleType.Required, localized.casino),
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.casino, 0)},
                                validate: (value, values) => {
                                    const maxValue = maxLimits[values?.commission_subtypes?.casino] ?? 100;
                                    return value > maxValue ? validationFormatter(RuleType.Max, localized.casino, maxValue) : true;
                                },
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="spins"
                            rules={{
                                required: validationFormatter(RuleType.Required, localized.globalSpins),
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.globalSpins, 0)},
                                validate: (value, values) => {
                                    const maxValue = maxLimits[values?.commission_subtypes?.spins] ?? 100;
                                    return value > maxValue ? validationFormatter(RuleType.Max, localized.globalSpins, maxValue) : true;
                                },
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableTextInternal
                                        value={field.value}
                                        onChange={field.onChange}
                                        type="number"
                                        mode={mode}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="sports"
                            rules={{
                                required: validationFormatter(RuleType.Required, localized.sports),
                                min: {value: 0, message: validationFormatter(RuleType.Min, localized.sports, 0)},
                                validate: (value, values) => {
                                    const maxValue = maxLimits[values?.commission_subtypes?.sports] ?? 100;
                                    return value > maxValue ? validationFormatter(RuleType.Max, localized.sports, maxValue) : true;
                                },
                            }}
                        />
                    </TableCell>
                </TableRow>
                <TableRow className={classes.tableRow}>
                    <HeaderCell>
                        <LocalizedText label={localized.commissionType} />
                    </HeaderCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableSelectInternal
                                        value={field.value}
                                        mode={subtypesMode}
                                        options={pokerCashSubTypesValues.map<SelectOption>(i => ({
                                            value: i,
                                            label: subTypesLocalization[i],
                                        }))}
                                        onSubmit={(value: PokerCashSubTypesViewModel) => {
                                            const ploSubtype = pokerCashToPloMapping[value];
                                            setValue('poker_cash.plo', !ploSubtype ? null : 0);
                                            setValue('commission_subtypes.poker_cash_plo', ploSubtype);
                                            field.onChange(value);
                                        }}
                                        className={classes.select}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="commission_subtypes.poker"
                            rules={{
                                validate: (value: PokerCashSubTypesViewModel) =>
                                    !isMasterAgent || pokerCashSubTypesValues.includes(value)
                                        ? true
                                        : validationFormatter(RuleType.Required, localized.pokerCash),
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableSelectInternal
                                        value={field.value}
                                        mode={subtypesMode}
                                        options={pokerMttSubTypesValues.map<SelectOption>(i => ({
                                            value: i,
                                            label: subTypesLocalization[i],
                                        }))}
                                        onSubmit={field.onChange}
                                        className={classes.select}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="commission_subtypes.poker_mtt"
                            rules={{
                                validate: (value: PokerMttSubTypesViewModel) =>
                                    !isMasterAgent || pokerMttSubTypesValues.includes(value)
                                        ? true
                                        : validationFormatter(RuleType.Required, localized.pokerMtt),
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <LocalizedText label={subTypesLocalization[pokerPloCashSubtype] ?? localized.none} />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableSelectInternal
                                        value={field.value}
                                        mode={subtypesMode}
                                        options={casinoSubTypesValues.map<SelectOption>(i => ({
                                            value: i,
                                            label: subTypesLocalization[i],
                                        }))}
                                        onSubmit={value => {
                                            field.onChange(value);
                                            trigger('casino_share');
                                        }}
                                        className={classes.select}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="commission_subtypes.casino"
                            rules={{
                                validate: (value: CasinoSubTypesViewModel) =>
                                    !isMasterAgent || casinoSubTypesValues.includes(value)
                                        ? true
                                        : validationFormatter(RuleType.Required, localized.casino),
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableSelectInternal
                                        value={field.value}
                                        mode={subtypesMode}
                                        options={globalSpinsSubTypesValues.map<SelectOption>(i => ({
                                            value: i,
                                            label: subTypesLocalization[i],
                                        }))}
                                        onSubmit={field.onChange}
                                        className={classes.select}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="commission_subtypes.spins"
                            rules={{
                                validate: (value: GlobalSpinsSubTypesViewModel) =>
                                    !isMasterAgent || globalSpinsSubTypesValues.includes(value)
                                        ? true
                                        : validationFormatter(RuleType.Required, localized.globalSpins),
                            }}
                        />
                    </TableCell>
                    <TableCell>
                        <Controller
                            control={control}
                            render={({field, fieldState}) => (
                                <>
                                    <EditableSelectInternal
                                        value={field.value}
                                        mode={subtypesMode}
                                        options={sportsSubTypesValues.map<SelectOption>(i => ({
                                            value: i,
                                            label: subTypesLocalization[i],
                                        }))}
                                        onSubmit={value => {
                                            field.onChange(value);
                                            trigger('sports');
                                        }}
                                        className={classes.select}
                                    />
                                    {fieldState?.error ? <FormError>{fieldState?.error?.message}</FormError> : null}
                                </>
                            )}
                            name="commission_subtypes.sports"
                            rules={{
                                validate: (value: SportsSubTypesViewModel) =>
                                    !isMasterAgent || sportsSubTypesValues.includes(value)
                                        ? true
                                        : validationFormatter(RuleType.Required, localized.sports),
                            }}
                        />
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );
}
